import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface CancelConsultationInput {
  consultationId: string;
  reason?: string;
}

export interface CancelConsultationResult {
  ok: true;
  consultationId: string;
  refunds: Array<{
    party: 'seeker' | 'other_party';
    payment_intent: string;
    refund_id?: string;
    error?: string;
  }>;
}

/**
 * Calls the `cancel-consultation` edge function which:
 *   - validates the 24h cancellation window,
 *   - flips status to 'cancelled' (DB trigger frees availability_slots),
 *   - issues automatic Stripe refunds for any paid side.
 *
 * The hook surfaces structured errors so the UI can show a friendly message
 * for the most common refusal (`cancellation_window_closed`).
 */
export const useCancelConsultationViaEdge = () => {
  const queryClient = useQueryClient();

  return useMutation<CancelConsultationResult, Error, CancelConsultationInput>({
    mutationFn: async ({ consultationId, reason }) => {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) throw new Error('Vous devez être connecté.');

      const { data, error } = await supabase.functions.invoke('cancel-consultation', {
        body: { consultationId, reason },
      });

      if (error) {
        // supabase-js wraps non-2xx as FunctionsHttpError; pull the JSON payload.
        const fnContext = (error as { context?: Response }).context;
        if (fnContext && typeof fnContext.json === 'function') {
          try {
            const payload = await fnContext.json();
            const msg = payload?.message || payload?.error || error.message;
            const wrapped = new Error(msg);
            (wrapped as Error & { code?: string }).code = payload?.error;
            throw wrapped;
          } catch {
            throw error;
          }
        }
        throw error;
      }

      return data as CancelConsultationResult;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['consultations'] });
      queryClient.invalidateQueries({ queryKey: ['myConsultations'] });
      queryClient.invalidateQueries({ queryKey: ['mediatorConsultations'] });
      queryClient.invalidateQueries({ queryKey: ['availableSlots'] });
      queryClient.invalidateQueries({ queryKey: ['availabilitySlots'] });
      queryClient.invalidateQueries({ queryKey: ['publicMediatorSlots'] });
      queryClient.invalidateQueries({ queryKey: ['searchAvailabilitySlots'] });
      queryClient.invalidateQueries({ queryKey: ['widgetSearchAvailabilitySlots'] });
    },
  });
};

/**
 * Pure helper — exposed for testing. Returns true when the meeting is at
 * least 24 hours away (or has no scheduled date yet).
 */
export const isWithinCancellationWindow = (
  scheduledDate?: string | null,
  scheduledTime?: string | null,
  now: Date = new Date(),
): boolean => {
  if (!scheduledDate || !scheduledTime) return true;
  const t = scheduledTime.length === 5 ? scheduledTime + ':00' : scheduledTime;
  // Treat as Europe/Paris local: build an ISO with explicit Paris offset.
  // We approximate by trusting the browser's locale offset for the date —
  // sufficient for client-side gating; the server-side trigger is the SoT.
  const local = new Date(`${scheduledDate}T${t}`);
  const msUntil = local.getTime() - now.getTime();
  return msUntil >= 24 * 60 * 60 * 1000;
};
